import { defineMessages } from 'react-intl';
import {
  creative as creativeConstants,
  text as textConstants,
} from 'content-creation-common/constants';

export const {
  CONTENT_TYPE,
  CREATIVE_FORMAT_VERSION_V2,
  CREATIVE_FORMAT_VERSION_V3,
  CREATIVE_FORMAT_VERSION_V4,
  CURRENT_CREATIVE_FORMAT_VERSION,
  CREATIVE_MODES,
  EASE_IN_CUBIC,
  EASE_IN_OUT_CUBIC,
  EASE_OUT_CUBIC,
  EASING_FUNCTIONS,
  LINEAR,
  MEDIA_TYPE,
  PLAYBACK_FRAMES_PER_SECOND,
  SHAPE,
  SHAPE_PROPERTIES,
  STEP,
  VALID_CREATIVE_MODES,
  VALID_EASING_FUNCTIONS,
} = creativeConstants;
export const {
  DEFAULT_TEXT_EFFECTS_OBJECTS,
  HIGHLIGHT_SIZE_OPTIONS,
  TEXT_ALIGNMENT,
  TEXT_EFFECTS,
} = textConstants;

/* ------------- Dev Config ------------- */

// All environment variables
export const ENVIRONMENT = process.env;

export const DEV = ENVIRONMENT.NODE_ENV === 'development';
export const STAGE = ENVIRONMENT.NODE_ENV === 'stage';
export const PRODUCTION = ENVIRONMENT.NODE_ENV === 'production';
export const TEST = ENVIRONMENT.NODE_ENV === 'test';
export const HOT = ENVIRONMENT.HOT === 'true';
export const DEBUG_CREATIVE_SUITE_API = ENVIRONMENT.DEBUG_CREATIVE_SUITE_API;
// You may use CreativeSuite/util/video/copyMediaChunksToPublicFolder.sh to add more test videos to the public bucket
export const DEBUG_REFORMATTING_TEST_VIDEO =
  ENVIRONMENT.DEBUG_REFORMATTING_TEST_VIDEO;
export const DEBUG_SCRAPER_API = ENVIRONMENT.DEBUG_SCRAPER_API;
export const REVIEW_AND_FINISH_FLOW_ENABLED =
  ENVIRONMENT.REVIEW_AND_FINISH_FLOW_ENABLED;
export const IMAGE_CENTERING_ENABLED = ENVIRONMENT.IMAGE_CENTERING_ENABLED;
export const PUBLIC_MEDIA_BUCKET_PATH = ENVIRONMENT.PUBLIC_MEDIA_BUCKET_PATH;
export const PUBLIC_SENTRY_DATA_SOURCE_NAME =
  ENVIRONMENT.PUBLIC_SENTRY_DATA_SOURCE_NAME;
/* Don't want this escaping out of DEV without really meaning it */
export const DISABLE_PERMISSION_CHECKING =
  DEV && ENVIRONMENT.DISABLE_PERMISSION_CHECKING;
export const LOG_ALL = ENVIRONMENT.LOG_ALL;
export const IS_STABLE = ENVIRONMENT.IS_STABLE;

// Build Version
export const BUILD_VERSION = TEST ? '0.0.0' : ENVIRONMENT.BUILD_VERSION;

export const BUILD_NAME = ENVIRONMENT.BUILD_NAME;
export const BUILD_NAME_STABLE = 'stable';
export const SNAP_PUB_FRIENDS = ENVIRONMENT.SNAP_PUB_FRIENDS;

// Check for embedded flag, should be on window but possibly also environment
// Note(omegdadi): Using `self` instead of `window` here intentionally, window
// is not defined inside a web worker.

// If the hostname does not match the SSO host name, assume we are on Google Auth for now
// FIXME: Use another way to tell.
export const IS_SSO =
  ENVIRONMENT.SSO_HOST_LIST &&
  ENVIRONMENT.SSO_HOST_LIST.includes(self.location.hostname);
export const ACCOUNT_HOST = ENVIRONMENT.ACCOUNT_HOST;
export const SSO_CLIENT_ID = ENVIRONMENT.SSO_CLIENT_ID;
export const SSO_CONTINUE_ENDPOINT = ENVIRONMENT.SSO_CONTINUE_ENDPOINT;
export const GENERIC_SUCCESS_JSON_URL = ENVIRONMENT.GENERIC_SUCCESS_JSON_URL;

export const GLOBAL_NAV_CONFIG_BUCKET = ENVIRONMENT.GLOBAL_NAV_CONFIG_BUCKET;
export const ALLOW_PUBLISHERS = TEST || ENVIRONMENT.ALLOW_PUBLISHERS;
export const ALLOW_SNAP_PRO = TEST || ENVIRONMENT.ALLOW_SNAP_PRO;
export const ADS_TERMS_AND_CONDITIONS_URL =
  ENVIRONMENT.ADS_TERMS_AND_CONDITIONS_URL;
export const ADS_GUIDELINES_URL = ENVIRONMENT.ADS_GUIDELINES_URL;
export const GLOBAL_ID = 'GLOBAL';
export const OUR_STORIES_PUBLISHER_ID = 'OUR_STORIES';
export const WILDCARD_ID = '*';
export const GROUP_TYPE = {
  ORGANIZATIONS: 'organizations',
  PUBLISHERS: 'publishers',
  SNAP_PRO: 'snap-pro-profiles',
};

export const ACCOUNT_TYPE = {
  AD_ACCOUNT: 'adaccounts',
  ORGANIZATION: 'organizations',
  PUBLISHER: 'publishers',
  SNAP_PRO: 'snap-pro-profiles',
};

export const SINGULAR_ACCOUNT_TYPE = {
  AD_ACCOUNT: 'adaccount',
  ORGANIZATION: 'organization',
  PUBLISHER: 'publisher',
  SNAP_PRO: 'snap-pro-profile',
};

// Enables video-reformatting features
export const EPISODE_CREATION_ENABLED = ENVIRONMENT.EPISODE_CREATION_ENABLED;

// Only Orgs Created AFTER this date will be eligable to have Simple Flow
export const SIMPLE_FLOW_ORG_START_DATE = new Date(
  ENVIRONMENT.SIMPLE_FLOW_ORG_START_DATE,
);
export const SIMPLE_FLOW_ORG_START_DATE_2 = new Date(
  ENVIRONMENT.SIMPLE_FLOW_ORG_START_DATE_2,
);

// First cohort, started on SIMPLE_FLOW_ORG_START_DATE
export const SIMPLE_FLOW_ADS_ROLLOUT_PERCENTAGE =
  ENVIRONMENT.SIMPLE_FLOW_ADS_ROLLOUT_PERCENTAGE; // In decimal

// Second cohort, started on SIMPLE_FLOW_ORG_START_DATE_2
export const SIMPLE_FLOW_ADS_ROLLOUT_PERCENTAGE_2 =
  ENVIRONMENT.SIMPLE_FLOW_ADS_ROLLOUT_PERCENTAGE_2; // In decimal

// Publishers
export const SIMPLE_FLOW_PUBLISHER_ROLLOUT_PERCENTAGE =
  ENVIRONMENT.SIMPLE_FLOW_PUBLISHER_ROLLOUT_PERCENTAGE; // In decimal

// Feature flags
export const FEATURE_FLAGS = ENVIRONMENT.FEATURE_FLAGS || {};

// Video Ingestion Service Host URL
export const VIDEO_INGEST_HOST = ENVIRONMENT.VIDEO_INGEST_HOST;

/*
 These come from JAVA
 com/snapchat/creativesuite/api/security/XSRF.java
 public static final String COOKIE_NAME = "xsrfToken";
 public static final String HEADER_NAME = "X-Xsrf-Token";
 */
export const CROSS_ORIGIN_ANONYMOUS = 'anonymous';
export const CROSS_ORIGIN_USE_CREDENTIALS = 'use-credentials';

export const GLOBAL_NAV_SNAP_PUBLISHER_TARGET_ID = 'SNAP_PUBLISHER';
export const HELP_CENTER_URL = ENVIRONMENT.HELP_CENTER_URL;
export const AD_MANAGER_SETUP_URL = IS_SSO
  ? ENVIRONMENT.AD_MANAGER_SETUP_URL_SSO
  : ENVIRONMENT.AD_MANAGER_SETUP_URL_GOOGLE_AUTH;

export const MAX_COLOR_SUGGESTION = 5;

/* ------------- Enums ------------- */

export const QUERY_PARAMETER = {
  AD_ACCOUNT_ID: 'adAccountId',
  BUSINESS_PROFILE_ID: 'businessProfileId',
  CREATIVE_ID: 'id',
  DESCRIPTION: 'description',
  EDITION_ID: 'editionId',
  EDITOR_MODE: 'editorMode',
  EDITOR_SERVICE_MODE: 'editorServiceMode',
  IMAGE_URL: 'imageUrl',
  IMPORT_ASSET_ID: 'importAssetId',
  LAYER_ASSET_ID: 'layerAssetId',
  MEDIA_ID: 'mediaId',
  ORGANIZATION_ID: 'organizationId',
  PUBLISHER_ID: 'publisherId',
  REFERRER_URL: 'ref',
  SNAP_ID: 'snapId',
  SOURCE_CONTENT_URL: 'sourceContentUrl',
  TITLE: 'title',
  TRIM_MODE: 'trimMode',
  LOCALE: 'lang',
};

// These parameters are expected to remain in the query string as the user navigates routes or if the URL is updated.
export const PRESERVED_QUERY_PARAMETERS = [
  QUERY_PARAMETER.AD_ACCOUNT_ID,
  QUERY_PARAMETER.CREATIVE_ID,
  QUERY_PARAMETER.DESCRIPTION,
  QUERY_PARAMETER.EDITION_ID,
  QUERY_PARAMETER.EDITOR_MODE,
  QUERY_PARAMETER.EDITOR_SERVICE_MODE,
  QUERY_PARAMETER.IMAGE_URL,
  QUERY_PARAMETER.IMPORT_ASSET_ID,
  QUERY_PARAMETER.MEDIA_ID,
  QUERY_PARAMETER.ORGANIZATION_ID,
  QUERY_PARAMETER.PUBLISHER_ID,
  QUERY_PARAMETER.REFERRER_URL,
  QUERY_PARAMETER.SNAP_ID,
  QUERY_PARAMETER.TITLE,
  QUERY_PARAMETER.TRIM_MODE,
];

export const DEV_LOCALES = ['pseudo-accented', 'pseudo-bidi'];

export const MEDIA_GALLERY_CONTENT_TYPE = new Set([
  CONTENT_TYPE.AUDIO,
  CONTENT_TYPE.IMAGE,
  CONTENT_TYPE.SVG,
  CONTENT_TYPE.VIDEO,
]);

export const AUDIBLE_CONTENT_TYPES = new Set([
  CONTENT_TYPE.AUDIO,
  CONTENT_TYPE.VIDEO,
]);
export const INVISIBLE_CONTENT_TYPES = new Set([CONTENT_TYPE.AUDIO]);

const contentTypeLabelMessages = defineMessages({
  image: {
    defaultMessage: 'Image',
    description: 'Label for the image content type',
  },
  shape: {
    defaultMessage: 'Shape',
    description: 'Label for the shape content type',
  },
  svg: {
    defaultMessage: 'Image',
    description: 'Label for the SVG content type',
  },
  video: {
    defaultMessage: 'Video',
    description: 'Label for the video content type',
  },
  audio: {
    defaultMessage: 'Audio',
    description: 'Label for the audio content type',
  },
  text: {
    defaultMessage: 'Text',
    description: 'Label for the text content type',
  },
  group: {
    defaultMessage: 'Group',
    description:
      'Label for the group content type (a collection of more layers)',
  },
});

export const CONTENT_TYPE_LABEL = {
  [CONTENT_TYPE.AUDIO]: contentTypeLabelMessages.audio.defaultMessage, // FIXME This should switch on mediaType instead of contentType
  [CONTENT_TYPE.ELEMENT_EFFECT_GROUP]: contentTypeLabelMessages.group.defaultMessage,
  [CONTENT_TYPE.ELEMENT_GROUP]: contentTypeLabelMessages.group.defaultMessage,
  [CONTENT_TYPE.IMAGE]: contentTypeLabelMessages.image.defaultMessage,
  [CONTENT_TYPE.SHAPE]: contentTypeLabelMessages.shape.defaultMessage,
  [CONTENT_TYPE.SVG]: contentTypeLabelMessages.svg.defaultMessage,
  [CONTENT_TYPE.TEXT]: contentTypeLabelMessages.text.defaultMessage,
  [CONTENT_TYPE.VIDEO]: contentTypeLabelMessages.video.defaultMessage,
};

export const AUDIBLE_MEDIA_TYPES = new Set([
  MEDIA_TYPE.AUDIO,
  MEDIA_TYPE.VIDEO,
]);

export const NON_STATIC_MEDIA_TYPES = new Set([
  MEDIA_TYPE.AUDIO,
  MEDIA_TYPE.VIDEO,
]);

export const MEDIA_SOURCE = {
  STORY_KIT: 'STORY_KIT', // from story kit
  STORY_KIT_OUR_STORIES: 'STORY_KIT_OUR_STORIES', // from our stories
};

export const MEDIA_PROGRESS_STAGE = {
  DONE: 'DONE',
  PROCESSING: 'PROCESSING',
  UPLOADING: 'UPLOADING',
};

export const ZOOM_DIRECTION = {
  IN: 'IN',
  OUT: 'OUT',
};

export const SHAPE_NATURAL_HEIGHT_IN_PIXELS = 400;

export const ELEMENT_HANDLES = {
  TOP: 'TOP',
  LEFT: 'LEFT',
  BOTTOM: 'BOTTOM',
  RIGHT: 'RIGHT',
  TOP_LEFT: 'TOP_LEFT',
  TOP_RIGHT: 'TOP_RIGHT',
  BOTTOM_RIGHT: 'BOTTOM_RIGHT',
  BOTTOM_LEFT: 'BOTTOM_LEFT',
};

export const SECOND_IN_MILLISECONDS = 1000;
export const MINUTE_IN_SECONDS = 60;
export const MINUTE_IN_MILLISECONDS =
  SECOND_IN_MILLISECONDS * MINUTE_IN_SECONDS;
export const HOUR_IN_MILLISECONDS = MINUTE_IN_MILLISECONDS * 60;
export const DAY_IN_MILLISECONDS = HOUR_IN_MILLISECONDS * 24;
export const WEEK_IN_MILLISECONDS = DAY_IN_MILLISECONDS * 7;

const templateFilterSizeMessages = defineMessages({
  nofilter: {
    defaultMessage: 'Any Size',
    description: 'Label for template size filter - matches any size',
  },
  small: {
    defaultMessage: 'Small',
    description: 'Label for template size filter - matches small sizes',
  },
  medium: {
    defaultMessage: 'Medium',
    description: 'Label for template size filter - matches medium sizes',
  },
  large: {
    defaultMessage: 'Large',
    description: 'Label for template size filter - matches large sizes',
  },
});

export const TEMPLATE_FILTER_SIZES = {
  NONE: {
    value: 'NONE',
    label: templateFilterSizeMessages.nofilter,
    minArea: -Infinity,
    maxArea: Infinity,
  },
  SMALL: {
    value: 'SMALL',
    label: templateFilterSizeMessages.small,
    minArea: -Infinity,
    maxArea: 259200, // 1920 * 1080 / 8
  },
  MEDIUM: {
    value: 'MEDIUM',
    label: templateFilterSizeMessages.medium,
    minArea: 259201,
    maxArea: 691200, // 1920 * 1080 / 3
  },
  LARGE: {
    value: 'LARGE',
    label: templateFilterSizeMessages.large,
    minArea: 691201,
    maxArea: Infinity,
  },
};

export const TEXT_EFFECTS_MESSAGES = defineMessages({
  NONE: {
    defaultMessage: 'None',
    description: 'Label for text effects dropdown - none selected',
  },
  DROP_SHADOW: {
    defaultMessage: 'Drop Shadow',
    description: 'Label for text effects dropdown - drop shadow selected',
  },
  OUTER_GLOW: {
    defaultMessage: 'Outer Glow',
    description: 'Label for text effects dropdown - outer glow selected',
  },
  INNER_SHADOW: {
    defaultMessage: 'Inner Shadow',
    description: 'Label for text effects dropdown - inner shadow selected',
  },
  BOX_FILL: {
    defaultMessage: 'Box Fill',
    description:
      'Label for text effects dropdown - box fill (fills the whole background) selected',
  },
  LINE_FILL: {
    defaultMessage: 'Line Fill',
    description:
      'Label for text effects dropdown - line fill (fills behind each line of text) selected',
  },
  FIT_LINE_FILL: {
    defaultMessage: 'Fit Line Fill',
    description:
      'Label for text effects dropdown - fit line fill (fille behind each line of text according to text size) selected',
  },
  OUTLINE_STROKE: {
    defaultMessage: 'Outline Stroke',
    description: 'Label for text effects dropdown - outline stroke selected',
  },
  BEVEL: {
    defaultMessage: 'Bevel',
    description: 'Label for text effects dropdown - bevel selected',
  },
  DROP_SHADOW_OUTLINE: {
    defaultMessage: 'Drop Shadow with Outline',
    description:
      'Label for text effects dropdown - drop shadow with an outline selected',
  },
  HIGHLIGHT: {
    defaultMessage: 'Highlight',
    description:
      'Label for text effects dropdown - highlight (background drawn behind each line of text) selected',
  },
});

export const HIGHLIGHT_SIZE_OPTIONS_MESSAGES = defineMessages({
  [HIGHLIGHT_SIZE_OPTIONS.LINE]: {
    defaultMessage: 'Line Length',
    description:
      'Label for highlight option dropdown - line length (fill full width) selected',
  },
  [HIGHLIGHT_SIZE_OPTIONS.TEXT]: {
    defaultMessage: 'Text Length',
    description:
      'Label for highlight option dropdown - line length (fill text width only) selected',
  },
});

export const TEXT_EFFECTS_CONFIGURATIONS = {
  NONE: {
    blur: false,
    borderRadius: false,
    boxPadding: false,
    color: false,
    offset: false,
    opacity: false,
    size: false,
  },
  DROP_SHADOW: {
    blur: true,
    borderRadius: false,
    boxPadding: false,
    color: true,
    offset: true,
    opacity: true,
    size: false,
  },
  OUTER_GLOW: {
    blur: false,
    borderRadius: false,
    boxPadding: false,
    color: true,
    offset: false,
    opacity: true,
    size: true,
  },
  INNER_SHADOW: {
    blur: true,
    borderRadius: false,
    boxPadding: false,
    color: true,
    offset: true,
    opacity: false,
    size: false,
  },
  BOX_FILL: {
    blur: false,
    borderRadius: true,
    boxPadding: false,
    color: true,
    offset: false,
    opacity: true,
    size: false,
  },
  LINE_FILL: {
    blur: false,
    borderRadius: true,
    boxPadding: true,
    color: true,
    offset: false,
    opacity: true,
    size: true,
  },
  FIT_LINE_FILL: {
    blur: false,
    borderRadius: true,
    boxPadding: true,
    color: true,
    offset: false,
    opacity: true,
    size: true,
  },
  OUTLINE_STROKE: {
    blur: false,
    borderRadius: false,
    boxPadding: false,
    color: true,
    offset: false,
    opacity: false,
    size: true,
  },
  BEVEL: {
    blur: false,
    borderRadius: false,
    boxPadding: false,
    color: true,
    offset: false,
    opacity: false,
    size: true,
  },
  DROP_SHADOW_OUTLINE: {
    blur: true,
    borderRadius: false,
    boxPadding: false,
    color: true,
    offset: true,
    opacity: true,
    size: true,
  },
  HIGHLIGHT: {
    blur: false,
    borderRadius: true,
    boxPadding: true,
    color: true,
    offset: false,
    opacity: true,
    size: true,
  },
};

export const DEFAULT_FILL_COLOR = '#000000';
export const DEFAULT_LINE_SPACING = 1.2;

export const CONTROL_PANEL_MESSAGES = defineMessages({
  HIDE: {
    defaultMessage: 'HIDE',
    description: 'Action label that hides the control panel',
  },
  REDO: {
    defaultMessage: 'REDO',
    description: 'Action label for the Redo button',
  },
  SHOW: {
    defaultMessage: 'SHOW',
    description: 'Action label that shows the control panel',
  },
  TURN_KEYFRAME_CAPTURE_ON: {
    defaultMessage: 'ENABLE AUTOFRAME',
    description: 'Action label that enables our `Autoframe` feature',
  },
  TURN_KEYFRAME_CAPTURE_OFF: {
    defaultMessage: 'DISABLE AUTOFRAME',
    description: 'Action label that disables our `Autoframe` feature',
  },
  UNDO: {
    defaultMessage: 'UNDO',
    description: 'Action label for the Undo button',
  },
  ZOOM_IN: {
    defaultMessage: 'ZOOM IN',
    description: 'Action label for the Zoom In control',
  },
  ZOOM_OUT: {
    defaultMessage: 'ZOOM OUT',
    description: 'Action label for the Zoom Out control',
  },
});

export const PUBLISHING_TARGET = {
  ADS: 'ADS',
  DISCOVER: 'DISCOVER',
  NONE: 'NONE',
};

export const IS_PUBLISH_TO_DISCOVER_ENABLED =
  ENVIRONMENT.ENABLE_PUBLISH_TO_DISCOVER;

export const DISCOVER_HOST = ENVIRONMENT.DISCOVER_HOST;
export const ADS_HOST = IS_SSO
  ? ENVIRONMENT.ADS_HOST_SSO
  : ENVIRONMENT.ADS_HOST_GOOGLE;

const embeddedAllowList = IS_SSO
  ? ENVIRONMENT.EMBEDDED_ALLOWLIST_SSO
  : ENVIRONMENT.EMBEDDED_ALLOWLIST_GOOGLE;
if (DEV || STAGE) {
  embeddedAllowList.push('localhost:3000');
}

export const EMBEDDED_HOST_ALLOWLIST = embeddedAllowList;

export const EXPORT_BUTTON_MESSAGES = defineMessages({
  PUBLISH: {
    defaultMessage: 'Done',
    description: 'Button label shown when export is complete',
  },
  PUBLISHING: {
    defaultMessage: 'Publishing…',
    description:
      'Button label (disabled state) shown while project is publishing',
  },
  PUBLISH_TO_ADS: {
    defaultMessage: 'Send to Ads Manager',
    description: 'Button label CTA to send media to ads manager',
  },
  DOWNLOAD: {
    defaultMessage: 'Download',
    description: 'Button label CTA to download rendered media',
  },
  DOWNLOADING: {
    defaultMessage: 'Downloading…',
    description: 'Button label (disabled state) while project is downloading',
  },
  OPEN: {
    defaultMessage: 'Open Snap…',
    description:
      'Button label CTA to open snap in either ads manager or story studio',
  },
  REVIEW_CANCEL: {
    defaultMessage: 'Make Edits',
    description: 'Button label CTA to close modal and return to editing',
  },
  REVIEW_NEXT: {
    defaultMessage: 'Looks good',
    description:
      'Button label CTA indicating the customer wants to proceed with export',
  },
  REVIEW_PROCESSING: {
    defaultMessage: 'Building video…',
    description:
      'Button label (disabled state) indicating build progress on a project',
  },
  REVIEW_UPLOADING: {
    defaultMessage: 'Uploading media…',
    description:
      'Button label (disabled state) indicating that media is uploading',
  },
  OPEN_AD_MANAGER: {
    defaultMessage: 'Open Ads Manager…',
    description: 'Button label CTA to open exported project in Ads Manager',
  },
  OPEN_DISCOVER: {
    defaultMessage: 'Open Story Studio…',
    description: 'Button label CTA to open exported project in Story Studio',
  },
  CURRENT_ACCOUNT: {
    defaultMessage: 'Current account: {accountName}',
    description:
      'Label indicating which account id currently in context for export',
  },
});

export const CANVAS_ALIGNMENT = {
  NONE: 'NONE',
  HORIZONTAL_LEFT: 'HORIZONTAL_LEFT',
  HORIZONTAL_CENTER: 'HORIZONTAL_CENTER',
  HORIZONTAL_RIGHT: 'HORIZONTAL_RIGHT',
  VERTICAL_TOP: 'VERTICAL_TOP',
  VERTICAL_TOP_BUFFER: 'VERTICAL_TOP_BUFFER',
  VERTICAL_CENTER: 'VERTICAL_CENTER',
  VERTICAL_BOTTOM_BUFFER: 'VERTICAL_BOTTOM_BUFFER',
  VERTICAL_BOTTOM: 'VERTICAL_BOTTOM',
};

const adsTemplateCategoryLabels = defineMessages({
  DRIVE_INSTALLS: {
    defaultMessage: 'Drive Installs (ADVERTISER)',
  },
  DRIVE_TRAFFIC: {
    defaultMessage: 'Drive Traffic (ADVERTISER)',
  },
  DRIVE_VIEWS: {
    defaultMessage: 'Drive Views (ADVERTISER)',
  },
  GROW_AWARENESS: {
    defaultMessage: 'Grow Awareness (ADVERTISER)',
  },
  NONE_ADS: {
    defaultMessage: 'None - Ads (ADVERTISER)',
  },
});

const publisherTemplateCategoryLabels = defineMessages({
  DRIVE_TIME_SPENT: {
    defaultMessage: 'Drive Time Spent (PUBLISHER)',
    description: 'Dropdown category value for template filter screen',
  },
  DRIVE_SWIPE_UP: {
    defaultMessage: 'Drive Swipe Up (PUBLISHER)',
    description: 'Dropdown category value for template filter screen',
  },
  SEQUENTIAL: {
    defaultMessage: 'Sequential (PUBLISHER)',
    description: 'Dropdown category value for template filter screen',
  },
  NONE_PUBLISHER: {
    defaultMessage: 'None - Publisher (PUBLISHER)',
    description:
      'Dropdown category value for template filter screen - none selected for publisher accounts',
  },
});

const otherTemplateCategoryLabels = defineMessages({
  ACTIVE: {
    defaultMessage: 'Available Templates',
    description: 'Dropdown category value for template filter screen',
  },
  ALL: {
    defaultMessage: 'All',
    description: 'Dropdown category value for template filter screen',
  },
  NONE_ALL: {
    defaultMessage: 'None - All',
    description: 'Dropdown category value for template filter screen',
  },
  GROUP_ALL: {
    defaultMessage: 'Your Templates',
    description:
      'Dropdown category value for template filter screen - templates owned by customers current account',
  },
  GLOBAL: {
    defaultMessage: 'Starter Templates',
    description: 'Dropdown category value for template filter screen',
  },
  INACTIVE: {
    defaultMessage: 'Drafts',
    description:
      'Dropdown category value for template filter screen - in progress templates',
  },
});

export const colorPickerSwatchesLabels = defineMessages({
  CUSTOM: {
    defaultMessage: 'Custom',
    description: 'Label for custom color selection swatches',
  },
  RECOMMENDED: {
    defaultMessage: 'Recommended',
    description: 'Dropdown category value recommended color values',
  },
});

export const LAYER_WARNING_MESSAGES = defineMessages({
  LAYER_WARNING_TEMPLATE: {
    defaultMessage: 'Oh! You have to use your own media!',
    description:
      'Warning message when a customer needs to replace media with their own',
  },
  LAYER_WARNING_REPLACE: {
    defaultMessage: 'Please replace the media in this layer before editing.',
    description:
      'Instructional message when a customer needs to replace media with their own',
  },
  LAYER_WARNING_SCENES: {
    defaultMessage:
      'Please replace the media in this layer before trimming the video.',
    description: 'Message to customer that video media needs to be replaced',
  },
  LAYER_WARNING_UPLOAD_IN_PROGRESS: {
    defaultMessage: 'Please wait while the media is uploading.',
    description:
      'Message to customer to wait while media upload is in progress',
  },
  LAYER_WARNING_BUILD_ERROR: {
    defaultMessage: 'Oh! You have some invalid media in your layers!',
    description:
      'Error message shown when a particular media causes render issues',
  },
  LAYER_WARNING_TEMPLATE_DETAILS: {
    defaultMessage:
      'Please replace the media in these layers before you continue. Thanks!',
    description:
      'Warning message indicating there are multiple layers that need to be corrected',
  },
  LAYER_WARNING_BUILD_ERROR_DETAILS: {
    defaultMessage:
      'Something went wrong with the media in these layers, please try a different file or format. Thanks!',
    description:
      'Error messaging indicating that we could not use the media in the specified layers and to try other formats',
  },
  LAYER_WARNING_BUILD_ERROR_GENERIC: {
    defaultMessage:
      'Something went wrong when building your video. Please try again and contact support if the problem persists.',
    description: 'Error message for generic build issues',
  },
});

export const TEMPLATE_CATEGORIES = {
  ADS: adsTemplateCategoryLabels,
  PUBLISHER: publisherTemplateCategoryLabels,
  NONE_ADS: adsTemplateCategoryLabels.NONE_ADS,
  NONE_PUBLISHER: publisherTemplateCategoryLabels.NONE_PUBLISHER,
};
export const ACTIVE_GROUP_TEMPLATE_CATEGORY =
  otherTemplateCategoryLabels.ACTIVE;
export const GLOBAL_TEMPLATE_CATEGORY = otherTemplateCategoryLabels.GLOBAL;
export const ALL_TEMPLATE_CATEGORY = otherTemplateCategoryLabels.ALL;
export const DEFAULT_TEMPLATE_CATEGORY = otherTemplateCategoryLabels.NONE_ALL;
export const DEFAULT_GROUP_TEMPLATE_CATEGORY =
  otherTemplateCategoryLabels.GROUP_ALL;
export const INACTIVE_GROUP_TEMPLATE_CATEGORY =
  otherTemplateCategoryLabels.INACTIVE;
export const DEFAULT_TEMPLATE_NAME = 'New Template';
export const DEFAULT_TEMPLATE_DESCRIPTION = '';

export const DRAG_MEDIA_KEYS = {
  MEDIA: 'media',
  JOBID: 'jobid',
};

export const DRAG_AXIS_LOCKS = {
  X: 'X',
  Y: 'Y',
  NONE: 'NONE',
};

export const MEDIA_GALLERY_TABS = {
  MY_LIBRARY: 'MY_LIBRARY',
  IMPORT: 'IMPORT',
};

export const mediaGalleryTabTitles = defineMessages({
  [MEDIA_GALLERY_TABS.MY_LIBRARY]: {
    defaultMessage: 'My Library',
    description: 'Tab Label for media import panel',
  },
  [MEDIA_GALLERY_TABS.IMPORT]: {
    defaultMessage: 'Import',
    description: 'Tab Label for media import panel',
  },
});

/* ------------- Configurable Constants ------------- */
export const TOPSNAP_PREVIEW_SCREEN_HEIGHT = 667;
export const CANVAS_SNAP_PX = 7;
export const BUFFER_HEIGHT = 150;

export const MAX_EXTRACTION_RESOLUTION = 480;

export const FRAME_MANAGER_ENABLED = ENVIRONMENT.FRAME_MANAGER_ENABLED;
export const FRAMES_PER_SECOND_V1 = 30;
const DEFAULT_OUTPUT_MEDIA_FRAMES_PER_SECOND = 60;
const DEFAULT_EPISODE_OUTPUT_MEDIA_FRAMES_PER_SECOND = 30;
export const OUTPUT_MEDIA_FRAMES_PER_SECOND =
  ENVIRONMENT.OUTPUT_MEDIA_FRAMES_PER_SECOND ||
  DEFAULT_OUTPUT_MEDIA_FRAMES_PER_SECOND;
export const EPISODE_OUTPUT_MEDIA_FRAMES_PER_SECOND =
  ENVIRONMENT.EPISODE_OUTPUT_MEDIA_FRAMES_PER_SECOND ||
  DEFAULT_EPISODE_OUTPUT_MEDIA_FRAMES_PER_SECOND;

export const DEFAULT_CREATIVE_LENGTH = 5 * PLAYBACK_FRAMES_PER_SECOND - 1; // 5 seconds
export const DEFAULT_PUBLISHER_CREATIVE_LENGTH =
  10 * PLAYBACK_FRAMES_PER_SECOND - 1; // 10 seconds

export const MAX_EPISODE_FILE_SIZE_MB = ENVIRONMENT.MAX_EPISODE_FILE_SIZE_MB;
export const MAX_EPISODE_FILE_SIZE_BYTES =
  MAX_EPISODE_FILE_SIZE_MB * 1024 * 1024;
export const MAX_MEDIA_DURATION_SECONDS =
  ENVIRONMENT.MAX_MEDIA_DURATION_SECONDS;

export const BUILD_POLL_MAX = ENVIRONMENT.BUILD_POLL_MAX;
export const BUILD_POLL_MAX_FOR_PUBLISHING =
  ENVIRONMENT.BUILD_POLL_MAX_FOR_PUBLISHING;
export const BUILD_POLL_MAX_FOR_VIDEO_REFORMATTING =
  ENVIRONMENT.BUILD_POLL_MAX_FOR_VIDEO_REFORMATTING;
export const BUILD_POLL_MAX_FOR_ZIP_VIDEOS =
  ENVIRONMENT.BUILD_POLL_MAX_FOR_ZIP_VIDEOS;

const DEFAULT_MIN_MEDIA_SYNC_TIMEOUT_IN_MINUTES = 1;
const MIN_MEDIA_SYNC_TIMEOUT_IN_MINUTES =
  ENVIRONMENT.MIN_MEDIA_SYNC_TIMEOUT_IN_MINUTES ||
  DEFAULT_MIN_MEDIA_SYNC_TIMEOUT_IN_MINUTES;
export const MIN_MEDIA_SYNC_TIMEOUT =
  MIN_MEDIA_SYNC_TIMEOUT_IN_MINUTES * MINUTE_IN_MILLISECONDS;
export const PHOTO_AD_SYNC_TIMEOUT =
  ENVIRONMENT.PHOTO_AD_SYNC_TIMEOUT_IN_SECONDS * SECOND_IN_MILLISECONDS;
export const AUTO_SNAP_TIMEOUT_IN_SECONDS =
  ENVIRONMENT.AUTO_SNAP_TIMEOUT_IN_SECONDS;

export const IMPORTED_SNAP_BACKGROUND_CREATIVE_FIELD =
  ENVIRONMENT.IMPORTED_SNAP_BACKGROUND_CREATIVE_FIELD;
export const IMPORTED_SNAP_OVERLAY_LAYER_CREATIVE_FIELD =
  ENVIRONMENT.IMPORTED_SNAP_OVERLAY_LAYER_CREATIVE_FIELD;

export const FONT_LOADER_TIMEOUT = 60 * SECOND_IN_MILLISECONDS;
export const GLOBAL_FONT_CSS_URL = '/api/v1/fonts2/css';
export const ELEMENT_SNAP_PX = 5;

export const DEFAULT_ELEMENT_COLOR_CLASS = 'element-purple';
export const ELEMENT_COLOR_CLASSES = {
  [CONTENT_TYPE.AUDIO]: 'element-yellow',
  [CONTENT_TYPE.ELEMENT_EFFECT_GROUP]: 'element-tan', // should not be used
  [CONTENT_TYPE.ELEMENT_GROUP]: 'element-tan',
  [CONTENT_TYPE.IMAGE]: 'element-red',
  [CONTENT_TYPE.SHAPE]: 'element-light-blue',
  [CONTENT_TYPE.SVG]: 'element-red',
  [CONTENT_TYPE.TEXT]: 'element-green',
  [CONTENT_TYPE.VIDEO]: 'element-orange',
};

export const BASE_HASH = 5178;

export const POLL_RATE_MILLIS = {
  GET_MEDIA: 1000,
  BUILD_CREATIVE: 2000,
  PUBLISH_CREATIVE: 2000,
  REVIEW_CREATIVE_PLAYBACK: 100,
  ZIP_VIDEOS: 2000,
};

export const UPLOAD_PROGRESS_PERCENTAGE_CAP = 0.8;
export const BUILD_PROGRESS_PERCENTAGE_CAP = 0.8;

export const MAX_RETRIES_THUMBNAIL = 40;

export const ELEMENT_LIST_THUMBNAIL_SIZE = 72;
export const MEDIA_THUMBNAIL_SIZE = 76;
export const MEDIA_THUMBNAIL_PREVIEW_SIZE = 128;

export const STOCK_MEDIA_SEARCH_RESULT_AMMOUNT = 12; // Keep this a multiple of 4 so it renders nicely in the Media Gallery
export const STOCK_MEDIA_URL_FRAGMENT = 'gettyimages';

export const DEFAULT_TOOLTIP_DELAY = 0.5; // in seconds

export const AUTO_SAVE_DELAY = 500;
export const AUTO_SAVE_PERIOD =
  ENVIRONMENT.AUTO_SAVE_DURATION_SECONDS * SECOND_IN_MILLISECONDS;

export const ZOOM_MIN = 0.1;
export const ZOOM_MAX = 1.0;

export const SCALE_MAX = 3.0;

export const ARROW_NUDGE_DISTANCE = 1;
export const ARROW_NUDGE_LARGE_DISTANCE = 10;

export const ERROR_CALL_TO_ACTION_MAP = defineMessages({
  RETRY: {
    name: 'RETRY',

    defaultMessage: 'Please try again.',
    isDismissible: true,
    description: 'Call to action to retry whatever action caused the error',
  },
  DIFFERENT_ACCOUNT: {
    name: 'DIFFERENT_ACCOUNT',

    defaultMessage:
      'Please select a different account or refresh the page and try again.',
    isDismissible: true,
    description:
      'Warning message indicating that the customer should selected a different account',
  },
  REFRESH: {
    name: 'REFRESH',

    defaultMessage: 'Please refresh the page and try again.',
    isDismissible: false,
    description: 'Warning message asking the customer to refresh the page.',
  },
  CHECK_CONNECTION: {
    name: 'CHECK_CONNECTION',

    defaultMessage:
      "Please make sure you're connected to the Internet and reload this page.",
    isDismissible: false,
    description:
      'Warning message asking the customer to check their internet connection',
  },
  CONTACT_SUPPORT: {
    name: 'CONTACT_SUPPORT',

    defaultMessage: 'Please contact support.',
    isDismissible: true,
    description: 'Warning message asking customer to contact support',
  },
  CONTACT_ADMINISTRATOR_TO_SAVE_TEMPLATES: {
    name: 'CONTACT_ADMINISTRATOR_TO_SAVE_TEMPLATES',

    defaultMessage:
      'Please contact your administrator and request permission to save templates.',
    isDismissible: true,
    description:
      'Warning message instructing customer to contact support about template editing permissions',
  },
  CONTACT_ADMINISTRATOR_TO_DELETE_TEMPLATES: {
    name: 'CONTACT_ADMINISTRATOR_TO_DELETE_TEMPLATES',

    defaultMessage:
      'Please contact your administrator and request permission to delete templates.',
    isDismissible: true,
    description:
      'Warning message instructing customer to contact support about template editing permissions',
  },
  CONTACT_ADMINISTRATOR_TO_SAVE_SHARED_ASSETS: {
    // TODO (caskew): Figure out what this means.
    name: 'CONTACT_ADMINISTRATOR_TO_SAVE_SHARED_ASSETS',

    defaultMessage:
      'Please contact your administrator and request permission to save shared assets.',
    isDismissible: true,
    description:
      'Warning message instructing customer to contact support about shared asset permissions',
  },
  CONTACT_ADMINISTRATOR_TO_VIEW_SNAPS: {
    name: 'CONTACT_ADMINISTRATOR_TO_VIEW_SNAPS',

    defaultMessage:
      'Please contact your administrator and request permission to view Snaps.',
    isDismissible: false,
    description:
      'Warning message instructing customer to contact support about editing projects',
  },
  CONTACT_ADMINISTRATOR_TO_SAVE_SNAPS: {
    name: 'CONTACT_ADMINISTRATOR_TO_SAVE_SNAPS',

    defaultMessage:
      'Please contact your administrator and request permission to save Snaps.',
    isDismissible: true,
    description:
      'Warning message instructing customer to contact support about editing projects',
  },
  CONTACT_ADMINISTRATOR_TO_DELETE_SNAPS: {
    name: 'CONTACT_ADMINISTRATOR_TO_DELETE_SNAPS',

    defaultMessage:
      'Please contact your administrator and request permission to delete Snaps.',
    isDismissible: true,
    description:
      'Warning message instructing customer to contact support about editing projects',
  },
  CONTACT_ADMINISTRATOR_TO_DOWNLOAD_SNAPS: {
    name: 'CONTACT_ADMINISTRATOR_TO_DOWNLOAD_SNAPS',

    defaultMessage:
      'Please contact your administrator and request permission to download Snaps.',
    isDismissible: true,
    description:
      'Warning message instructing customer to contact support about downloading projects',
  },
  CONTACT_ADMINISTRATOR_TO_PUBLISH_SNAPS: {
    name: 'CONTACT_ADMINISTRATOR_TO_PUBLISH_SNAPS',

    defaultMessage:
      'Please contact your administrator and request permission to publish Snaps.',
    isDismissible: true,
    description:
      'Warning message instructing customer to contact support about publishing projects',
  },
  TRY_HTTPS: {
    name: 'TRY_HTTPS',

    defaultMessage: 'Please try a URL that starts with "HTTPS".',
    isDismissible: true,
    description: 'Warning message instructing customer to try a secure URL',
  },
  TRY_WWW: {
    name: 'TRY_WWW',

    defaultMessage: 'Did you forget to include the "www"?',
    isDismissible: true,
    description:
      'Warning message instructing customer to try a different import URL',
  },
  TRY_ANOTHER_IMAGE_FORMAT: {
    name: 'TRY_ANOTHER_IMAGE_FORMAT',

    defaultMessage:
      'Please convert your image file to be an SVG, PNG, JPG, or GIF.',
    isDismissible: true,
    description:
      'Warning message instructing customer to try a different image format',
  },
  TRY_ANOTHER_MEDIA_FORMAT: {
    name: 'TRY_ANOTHER_MEDIA_FORMAT',

    defaultMessage: 'Please try a different media format.',
    isDismissible: true,
    description:
      'Warning message instructing customer to try a different media format',
  },
  TRY_DIFFERENT_TEMPLATE: {
    name: 'TRY_DIFFERENT_TEMPLATE',

    defaultMessage: 'Please try a different template.',
    isDismissible: true,
    description:
      'Warning message instructing customer to try a different template',
  },
  TRY_DIFFERENT_FILE: {
    name: 'TRY_DIFFERENT_FILE',

    defaultMessage: 'Please try uploading a different file.',
    isDismissible: true,
    description:
      'Warning message instructing customer to try uploading a different file',
  },
  TRY_DIFFERENT_FILE_REFRESH: {
    name: 'TRY_DIFFERENT_FILE',

    defaultMessage:
      'Please refresh the page and try uploading a different file.',
    isDismissible: false,
    description:
      'Warning message instructing customer to try reloading the page and uploading a different file',
  },
  TRY_DIFFERENT_MEDIA: {
    name: 'TRY_DIFFERENT_MEDIA',

    defaultMessage:
      'Please replace the media in these layers before you continue.',
    isDismissible: true,
    description:
      'Warning message instructing customer to replace media in layers with their own',
  },
  TRY_DIFFERENT_URL: {
    name: 'TRY_DIFFERENT_URL',

    defaultMessage: 'Please try a different URL.',
    isDismissible: true,
    description: 'Warning message instructing customer to try a different URL',
  },
});

export const ERROR_MAP = defineMessages({
  DEFAULT: {
    name: 'Default',

    defaultMessage: 'We encountered an error.',
    isDismissible: true,
    description: 'Generic error message',
  },
  DOWNLOAD: {
    name: 'DOWNLOAD',

    defaultMessage: "We couldn't download your video.",
    isDismissible: true,
    description: 'Error message - we could not download the video',
  },
  UPLOAD_ERROR: {
    name: 'UPLOAD_ERROR',

    defaultMessage: "We couldn't upload your media.",
    isDismissible: true,
    description: 'Error message - we could not upload the video',
  },
  UPLOAD_TIMEOUT: {
    name: 'UPLOAD_TIMEOUT',

    defaultMessage: 'It took too long to finalize your upload.',
    isDismissible: true,
    description: 'Error message - we could not finalize the media upload',
  },
  TIMEOUT: {
    name: 'TIMEOUT',

    defaultMessage:
      "We couldn't contact our server. Please check your Internet connection.",
    isDismissible: true,
    description: 'Error message - we could not connect to the internet',
  },
  SAVE_CREATIVE: {
    name: 'SAVE_CREATIVE',

    defaultMessage: "We couldn't save your project.", // TODO (caskew): Make these SAVE/BUILD things one message?
    isDismissible: true,
    description:
      'Error message - we could not save the project (after retrying)',
  },
  BUILD_CREATIVE_DURING_SAVE: {
    name: 'BUILD_CREATIVE_DURING_SAVE',

    defaultMessage: "We couldn't build your video.",
    isDismissible: true,
    description: 'Error message - we could not build the video',
  },
  LOAD_USER_DATA: {
    name: 'LOAD_USER_DATA',

    defaultMessage: "We couldn't load your account information.",
    isDismissible: false,
    description: 'Error message - we could not load the customers account info',
  },
  LOAD_USER_ORGANIZATIONS: {
    name: 'LOAD_USER_ORGANIZATIONS',

    defaultMessage: "We couldn't load your organizations.",
    isDismissible: true,
    description: 'Error message - we could not find the customers organization',
  },
  LOAD_USER_PERMISSIONS: {
    name: 'LOAD_USER_PERMISSIONS',

    defaultMessage: "We couldn't find your permissions.",
    isDismissible: true,
    description:
      'Error message - we could not determine the customers permissions',
  },
  LOAD_CREATIVE: {
    name: 'LOAD_CREATIVE',

    defaultMessage: "We couldn't load this project.",
    isDismissible: false,
    description: 'Error message - we could not load a project',
  },
  DELETE_CREATIVE: {
    name: 'DELETE_CREATIVE',

    defaultMessage: "We couldn't delete your project.",
    isDismissible: true,
    description: 'Error message - we could not delete a project',
  },
  LOAD_TEMPLATE: {
    name: 'LOAD_TEMPLATE',

    defaultMessage: "We couldn't load the template.",
    isDismissible: true,
    description: 'Error message - we could not load a template',
  },
  SAVE_TEMPLATE: {
    name: 'SAVE_TEMPLATE',

    defaultMessage: "We couldn't save the template.",
    isDismissible: true,
    description: 'Error message - we could not save a template',
  },
  DELETE_TEMPLATE: {
    name: 'DELETE_TEMPLATE',

    defaultMessage: "We couldn't delete the template.",
    isDismissible: true,
    description: 'Error message - we could not delete a template',
  },
  LOAD_TEMPLATE_LIST: {
    name: 'LOAD_TEMPLATE_LIST',

    defaultMessage: "We couldn't load the template list.",
    isDismissible: true,
    description: 'Error message - we could not load the templates list',
  },
  LOAD_CREATIVE_LIST: {
    name: 'LOAD_CREATIVE_LIST',

    defaultMessage: "We couldn't load your projects.",
    isDismissible: true,
    description: 'Error message - we could not load the customers projects',
  },
  LOAD_MEDIA_LIST: {
    name: 'LOAD_MEDIA_LIST',

    defaultMessage: "We couldn't load your media.",
    isDismissible: true,
    description:
      'Error message - we could not download the customers media list',
  },
  UPLOAD_MEDIA: {
    name: 'UPLOAD_MEDIA',

    defaultMessage: "We couldn't upload the media.",
    isDismissible: true,
    description: 'Error message - we could not upload media',
  },
  PROCESS_MEDIA: {
    name: 'PROCESS_MEDIA',

    defaultMessage: "We couldn't process your media.",
    isDismissible: true,
    description: 'Error message - we could not process media post upload',
  },
  UNSUPPORTED_MEDIA: {
    name: 'UNSUPPORTED_MEDIA',

    defaultMessage: 'We do not support that file type!',
    isDismissible: true,
    description: 'Error message - we do not support the provided file type',
  },
  IMPORT_DISCOVER_ASSET: {
    name: 'IMPORT_DISCOVER_ASSET',

    defaultMessage: "We couldn't import the media.",
    isDismissible: true,
    description:
      'Error message - we could not import the media from Story Studio',
  },
  LOAD_FONTS: {
    name: 'LOAD_FONT_LIST',

    defaultMessage: "We couldn't load fonts.",
    isDismissible: true,
    description: 'Error message - we could not load fonts',
  },
  MEDIA_DURATION: {
    name: 'MEDIA_DURATION',

    defaultMessage:
      'The selected file must be less than {maxDuration} minutes long.',
    isDismissible: true,
    values: {
      maxDuration: Math.floor(
        ENVIRONMENT.MAX_MEDIA_DURATION_SECONDS / MINUTE_IN_SECONDS,
      ),
    },
    description:
      'Error message - the provided media must be shorter than our upper time limit',
  },
  SCRAPE_URL: {
    // Could not access URL.
    name: 'SCRAPE_URL',

    defaultMessage: "We couldn't find media at that URL.",
    isDismissible: true,
    description: 'Error message - we could not load an import URL',
  },
  SCRAPE_URL_INVALID: {
    // Bad url, can't do anything with it
    name: 'SCRAPE_URL_INVALID',

    defaultMessage: 'This does not appear to be a valid URL.',
    isDismissible: true,
    description:
      'Error message - we could not load an import URL because it is invalid',
  },
  SCRAPE_URL_NO_MEDIA: {
    // URL scraped, but no media
    name: 'SCRAPE_URL_NO_MEDIA',

    defaultMessage: "We couldn't import media from this URL.",
    isDismissible: true,
    description: 'Error message - we could not load an import URL',
  },
  SCRAPE_URL_RATE_LIMIT: {
    name: 'SCRAPE_URL_RATE_LIMIT',

    defaultMessage:
      'You reached the maximum number of imports allowed, please try again later',
    isDismissible: true,
    description:
      'Error message - we could not load an import URL because the customer has tried too many times',
  },
  STOCK_MEDIA_GENERIC_ERROR: {
    name: 'SCRAPE_MEDIA_GENERIC_ERROR',

    defaultMessage:
      'Sorry we were unable to process that request, please try again later.',
    isDismissible: true,
    description: 'Error message - we could not load an import URL',
  },
  STOCK_MEDIA_NO_SEARCH_QUERY: {
    name: 'STOCK_MEDIA_NO_SEARCH_QUERY',

    defaultMessage: 'Please specify something to search for in the search box.',
    isDismissible: true,
    description: 'Error message - we cannot search for an empty value',
  },
  STOCK_MEDIA_NO_RESULTS: {
    name: 'STOCK_MEDIA_NO_RESULTS',

    defaultMessage:
      "We weren't able to find results for that search query. Please try searching for something else.",
    isDismissible: true,
    description:
      'Error message - we could not find anything with the given search query',
  },
  NO_HTTP: {
    name: 'NO_HTTP',

    defaultMessage:
      'URLs that start with "HTTP" are not supported. Please use "HTTPS".',
    isDismissible: true,
    description: 'Error message - instruct the customer to try a secure URL',
  },
  PUBLISH: {
    name: 'PUBLISH',

    defaultMessage: "We couldn't publish your video.",
    isDismissible: true,
    description: 'Error message - we could not publish a video',
  },
  UNSUPPORTED_CREATIVE_FORMAT: {
    name: 'UNSUPPORTED_CREATIVE_FORMAT',

    defaultMessage: 'The application needs to be updated to open this project.',
    isDismissible: false,
    description:
      'Error message - the customers version of the app is out of date and they need to reload',
  },
  UNSUPPORTED_RESPONSE_VERSION: {
    name: 'UNSUPPORTED_RESPONSE_VERSION',

    defaultMessage: 'The application needs to be updated to continue.',
    isDismissible: false,
    description:
      'Error message - the customers version of the app is out of date and they need to reload',
  },
  ACCOUNT_NOT_FOUND: {
    name: 'ACCOUNT_NOT_FOUND',

    defaultMessage: 'We could not find your account.',
    isDismissible: true,
    description: 'Error message - the account is not accessible',
  },
  PUBLISH_CREATIVE: {
    name: 'PUBLISH_CREATIVE',

    defaultMessage: "We weren't able to publish your creative.",
    isDismissible: true,
    description: 'Error message - we could not publish the project',
  },
  DOWNLOAD_CREATIVE: {
    name: 'DOWNLOAD_CREATIVE',

    defaultMessage: "We weren't able to download your creative.",
    isDismissible: true,
    description: 'Error message - we could not download the project',
  },
  VIS_PROCESSING_ERROR: {
    name: 'VIS_PROCESSING_ERROR',

    defaultMessage: 'We were unable to process your video.',
    isDismissible: false,
    description: 'Error message - we could not process the customers video',
  },
  EPISODE_UPLOAD_TIMEOUT: {
    name: 'EPISODE_UPLOAD_TIMEOUT',

    defaultMessage: 'It took too long to finalize your episode.',
    isDismissible: false,
    description: 'Error message - the upload process took to long',
  },
  EPISODE_FILE_SIZE: {
    name: 'EPISODE_FILE_SIZE',

    defaultMessage: 'The selected file must be less than {maxFileSize}GB.',
    isDismissible: true,
    values: {
      maxFileSize: Math.floor(MAX_EPISODE_FILE_SIZE_MB / 1024),
    },
    description:
      'Error message - the given file was too large to upload (Gigabytes)',
  },
  DISCOVER_PUBLISHER: {
    name: 'DISCOVER_PUBLISHER',

    defaultMessage: "We couldn't load your Publisher.",
    isDismissible: true,
    description: 'Error message - the publisher account is not accessible',
  },
});

export const UI_LABELS = defineMessages({
  ANOTHER_GROUP_LABEL: {
    defaultMessage: 'another group',
    description: 'Collapsed group (org or publisher) label',
  },
  ADS_GUIDELINES_LINK: {
    defaultMessage: 'View Ad Guidelines',
    description: 'Link text for viewing ad guidlines',
  },
  ADS_QUICK_TIPS: {
    defaultMessage: 'Quick Tips',
    description: 'Label for toggling on/off quick tips',
  },
  REVIEW_BUILD_COMPLETE_ADS: {
    defaultMessage:
      'Nice work! Your video is ready to download or send to Ads Manager.',
    description:
      'Message - success building project that can be sent to Ads Manager',
  },
  REVIEW_BUILD_COMPLETE_DISCOVER: {
    defaultMessage:
      'Nice work! Your video is ready to download or send to Story Studio.',
    description:
      'Message - success building project that can be sent to Story Studio',
  },
  REVIEW_BUILD_COMPLETE_WITHOUT_PUBLISH: {
    defaultMessage: 'Nice work! Your video is ready to download.',
    description: 'Message - success building project that can be downloaded',
  },
  REVIEW_PUBLISH_COMPLETE_ADS: {
    defaultMessage: 'Well done! Your video has been sent to Ads Manager.',
    description: 'Message - success sending video to Ads Manager',
  },
  REVIEW_PUBLISH_COMPLETE_DISCOVER: {
    defaultMessage: 'Well done! Your video has been sent to Story Studio.',
    description: 'Message - success sending video to Story Studio',
  },
});

// Used in media gallery for processing rendering sizing.
export const MEDIA_ROW_HEIGHT = 80;
export const SIMPLE_MODE_MEDIA_ROW_HEIGHT = 150;
export const MEDIA_LIST_WIDTH = 440;
export const CHUNK_SIZE = 4;
export const MEDIA_GALLERY_THUMBNAIL_WIDTH = 72;
export const SIMPLE_MODE_CHUNK_SIZE = 3;
export const SIMPLE_MODE_MEDIA_GALLERY_THUMBNAIL_WIDTH =
  MEDIA_LIST_WIDTH / CHUNK_SIZE;

export const SUPPORTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/svg+xml',
];
export const IMAGE_ACCEPT = SUPPORTED_IMAGE_TYPES.join(',');
export const VIDEO_ACCEPT = 'video/mp4,video/quicktime,video/*';
export const AUDIO_ACCEPT = 'audio/mp3,audio/m4a';
export const VIDEO_REFORMATTING_ACCEPT = 'video/mp4,video/x-m4v,video/*';

export const DEFAULT_OUTPUT_VIDEO_FILENAME = 'snap_publisher_video.mp4';
/*
  !!!!!!!!!!!!
  THIS NUMBER SHOULD NOT BE CHANGED
  We only build this frame on each autosave/save and if we start looking for a different one all thumbnails would stop working.
 */
export const THUMBNAIL_FRAME_NUMBER_LEGACY = 2;
export const THUMBNAIL_FRAME_NUMBER = 45;
export const USER_MEDIA_THUMBNAIL_FRAME_NUMBER = 2;

export const MAX_THUMBNAIL_WIDTH_OR_HEIGHT = 4000;

export const SUBNAV_TAB = {
  SNAPS: 0,
  EPISODES: 1,
  TEMPLATES: 2,
  GLOBAL_TEMPLATES: 3,
};

const ACCOUNT_PICKER_LABELS = defineMessages({
  PUBLISHER_ORG_NAME: {
    defaultMessage: 'Publishers',
    description: 'Label for Publishers group in account picker',
  },
  SNAP_PRO_ORG_NAME: {
    defaultMessage: 'Snap Pro Profiles',
    description: 'Label for Snap Pro Profile names in account picker',
  },
  EDITOR_ROLE: {
    defaultMessage: 'EDITOR',
    description: 'Title for Editor-level permissions',
  },
  VIEWER_ROLE: {
    defaultMessage: 'VIEWER',
    description: 'Title for Viewer-level permissions',
  },
});
export const FAKE_PUBLISHER_ORG_ID = 'FAKE_PUBLISHER_ORG_ID';
export const FAKE_PUBLISHER_ORG = {
  id: FAKE_PUBLISHER_ORG_ID,
  name: ACCOUNT_PICKER_LABELS.PUBLISHER_ORG_NAME,
};
export const FAKE_SNAP_PRO_ORG_ID = 'FAKE_SNAP_PRO_ORG_ID';
export const FAKE_SNAP_PRO_ORG = {
  id: FAKE_SNAP_PRO_ORG_ID,
  name: ACCOUNT_PICKER_LABELS.SNAP_PRO_ORG_NAME,
};
export const ORG_ADMIN_ROLE = 'admin';
export const ACCOUNT_PICKER_ROLES = {
  EDITOR: ACCOUNT_PICKER_LABELS.EDITOR_ROLE,
  VIEWER: ACCOUNT_PICKER_LABELS.VIEWER_ROLE,
};

export const DEFAULT_COLORS = [
  '#d0021b',
  '#f5a623',
  '#f8e71c',
  '#8b572a',
  '#7ed321',
  '#417505',
  '#bd10e0',
  '#9013fe',
  '#4a90e2',
  '#50e3c2',
  '#b8e986',
  '#000000',
  '#4a4a4a',
  '#9b9b9b',
  '#ffffff',
];
// todo: make getter for this to return new clone every time it is used
export const DEFAULT_COLORS_OBJ = {
  colors: [...DEFAULT_COLORS],
  title: colorPickerSwatchesLabels.RECOMMENDED,
};
export const COLOR_PICKER_MAX_PER_ROW = 8;

export const TIMELINE_DRAG_TYPES = {
  ELEMENT: 'ELEMENT',
};

export const TIMELINE_BORDER = 2;
export const TIMELINE_DRAG_THRESHOLD = 1 / 2;
export const TIMELINE_GROUP_TOGGLE_CLOSE_DEG = -90;
export const TIMELINE_THUMBNAIL_INDENT = 20;
export const TIMELINE_DRAG_MARKER_WIDTH = 10;
export const TIMELINE_THUMBNAIL_DEFAULT_WIDTH = 50;
export const TIMELINE_DRAG_DIRECTION = {
  up: 'up',
  down: 'down',
};

export const TEST_DATA_URL =
  'data:image/gif;base64,R0lGODlhEAAQAMQAAORHHOVSKudfOulrSOp3WOyDZu6QdvCchPGolfO0o/XBs/' +
  'fNwfjZ0frl3/zy7////wAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACH5BAkAABAALAAAAAAQABA' +
  'AAAVVICSOZGlCQAosJ6mu7fiyZeKqNKToQGDsM8hBADgUXoGAiqhSvp5QAnQKGIgUhwFUYLCVDFCrKUE1lBavAViFIDlTImbKC5Gm2hB0SlBCBMQiB0UjIQA7';

export const NONE_TEMPLATE_ID = 'none';
/**
 * Which type of media is being edited in the editor, default is VIDEO
 * @type {{VIDEO: string, PHOTO: string}}
 */
export const EDITOR_MODES = {
  PHOTO: 'PHOTO',
  VIDEO: 'VIDEO',
};

export const VALID_EDITOR_MODES = new Set(Object.keys(EDITOR_MODES));

/**
 * Should the editor perform all build and publish actions? Default is FULL.
 * @type {{FULL: string, SELF: string}}
 */
export const EDITOR_SERVICE_MODES = {
  FULL: 'FULL',
  SELF: 'SELF',
};

export const VALID_EDITOR_SERVICE_MODES = new Set(
  Object.keys(EDITOR_SERVICE_MODES),
);

// This is here temporarily
export const THUMBNAIL_STYLES = {
  width: '100%',
  height: '100%',
  transform: 'scale(1)',
  transformOrigin: 'top left',
};

export const FEATURE_FLAG_ENTITY_AD_ACCOUNT = 'ad_account_id';
export const FEATURE_FLAG_ENTITY_BUSINESS_PROFILE_ID = 'business_profile_id';
export const FEATURE_FLAG_ENTITY_ORGANIZATION = 'organization_id';
export const FEATURE_FLAG_ENTITY_PUBLISHER = 'publisher_id';
export const FEATURE_FLAG_ENTITY_USER = 'user_id';

export const VALID_FEATURE_FLAG_ENTITY_TYPES = [
  FEATURE_FLAG_ENTITY_AD_ACCOUNT,
  FEATURE_FLAG_ENTITY_BUSINESS_PROFILE_ID,
  FEATURE_FLAG_ENTITY_ORGANIZATION,
  FEATURE_FLAG_ENTITY_PUBLISHER,
  FEATURE_FLAG_ENTITY_USER,
];

export const VALID_TYPES_FOR_FIT_MODE = [
  CONTENT_TYPE.IMAGE,
  CONTENT_TYPE.VIDEO,
  CONTENT_TYPE.SVG,
];

export const FIT_MODES = {
  FIT: 'FIT',
  FILL: 'FILL',
};

export const FIT_MODE_LABELS = defineMessages({
  FIT: {
    defaultMessage: 'Fit',
    description:
      'Label for Fit mode, where an image is fit within a given space',
  },
  FILL: {
    defaultMessage: 'Fill',
    description: 'Label for Fill mode, where an image fills a given space',
  },
  SELECT_OPTION: {
    defaultMessage: 'Select…',
    description: 'Label for fill mode dropdown when no option is selected',
  },
});

// We dont' need to draw faster than FRAMES_PER_SECOND
export const DRAG_THROTTLE_TIME = Math.floor(
  SECOND_IN_MILLISECONDS / PLAYBACK_FRAMES_PER_SECOND,
);
export const DRAG_MIN_DURATION_MS = 200;
export const DRAG_MIN_DISTANCE_PIXELS = 10;

export const TEMPLATE_LAYER_TYPES_WITH_BOUNDS = new Set([
  CONTENT_TYPE.IMAGE,
  CONTENT_TYPE.SVG,
  CONTENT_TYPE.VIDEO,
]);

export const DEFAULT_MEDIA_VARIANT_KEYFRAME_INTERVAL = 3;

export const TRANSFORM_END_DELAY = 2 * SECOND_IN_MILLISECONDS;

export const POSITION_UPDATE_RECORDING_GROUP =
  'POSITION_UPDATE_RECORDING_GROUP';
export const ALPHA_UPDATE_RECORDING_GROUP = 'ALPHA_UPDATE_RECORDING_GROUP';
export const ROTATION_UPDATE_RECORDING_GROUP =
  'ROTATION_UPDATE_RECORDING_GROUP';
export const SCALE_UPDATE_RECORDING_GROUP = 'SCALE_UPDATE_RECORDING_GROUP';

export const SORT_BY_OPTIONS = {
  MODIFIED_ON: 'MODIFIED_ON',
  NAME: 'NAME',
};

export const SORT_BY_LABELS = defineMessages({
  [SORT_BY_OPTIONS.MODIFIED_ON]: {
    defaultMessage: 'Sort by Date',
    description: 'Label for sort options, sort but date',
  },
  [SORT_BY_OPTIONS.NAME]: {
    defaultMessage: 'Sort by Name',
    description: 'Label for sort options, sort by name',
  },
});

export const OUTPUT_MEDIA_TYPES = {
  FULL_CREATIVE: 'FULL_CREATIVE',
  PARTIAL_CREATIVE: 'PARTIAL_CREATIVE',
  THUMBNAIL: 'THUMBNAIL',
};

export const DEBUG_BUFFER_TIME_SECONDS = 20;

export const COLOR_FILL_TYPES = {
  SOLID: 'SOLID',
  LINEAR: 'LINEAR',
};

export const COLOR_FILL_TYPES_MESSAGES = defineMessages({
  [COLOR_FILL_TYPES.SOLID]: {
    defaultMessage: 'Solid',
    description: 'Label for color fill options, solid color',
  },
  [COLOR_FILL_TYPES.LINEAR]: {
    defaultMessage: 'Linear',
    description: 'Label for color fill options, linear gradient',
  },
});

export const DEFAULT_COLOR_FILL_TYPE = COLOR_FILL_TYPES.SOLID;

export const NAV_HEIGHT_PX = 56; // same as $nav-height in variables.scss

export const STORY_STUDIO_DOCKING_OPTIONS = {
  BOTTOM: 'BOTTOM',
  MIDDLE: 'MIDDLE',
  NONE: 'NONE',
  TOP: 'TOP',
};
